import React from "react";
import type Highcharts from "highcharts";
import { getMainAxisSelectedPoints } from "@ui/Highcharts/plugins/stickyTooltip";
import { renderToString } from "react-dom/server";
import useQuantityYAxes, { getAxisIds } from "../../Chart/hooks/useQuantityYAxes";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import { FLAG_AXIS } from "../../Chart/series/getAnalysisFlagSerie";
import validateInterface from "@ui/lib/validateInterface";
import { GAP_POINT_METADATA, PointMetadata } from "../../Chart/PointMetadata";
import type { Options } from "@ui/Highcharts";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import useAnalysisXAxis from "../../Chart/hooks/useAnalysisXAxis";
import formatNumber from "@ui/i18n/formatNumber";
import { useQuantityNoDataIntervals } from "../../useNoDataIntervals";
import t from "../../../../i18n";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import type { Source } from "../../../../api/useSources";
import useGroupBreaks from "../../Chart/hooks/useGroupBreaks";
import Text from "@ui/Text";
import { useQuantityByName } from "../../../../api/useQuantities";
import { QuantityChartTooltip } from "../../Chart/ChartTooltip/data_symptom/QuantityChartTooltip";
import { SymptomQuantityChartTooltip } from "../../Chart/ChartTooltip/data_symptom/SymptomQuantityChartTooltip";
import { ChartTooltip } from "../../Chart/ChartTooltip";
import { AnalysisDateChartTooltip } from "../../Chart/ChartTooltip/common/AnalysisDateChartTooltip";
import { FlagChartTooltip } from "../../Chart/ChartTooltip/common/FlagChartTooltip";
import AxisTitle, { colorClassName } from "../../Chart/AxisTitle";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../Chart/hooks/useAnalysisSelectionPlotLines";

const useOptions = ({
  chartRef,
  selectedQuantities,
  series,
  analyses,
  project,
  source,
  timeZone,
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  selectedQuantities: SelectedQuantity[];
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesScatterOptions
  )[];
  analyses: ActiveAnalysis[];
  project: Project;
  source: Source;
  timeZone: string;
}): Options => {
  const quantitiesByName = useQuantityByName(project.id);
  const yAxisIds = React.useMemo(() => getAxisIds(selectedQuantities, false), [selectedQuantities]);
  const initialYAxisOptions = useYMinMax({ chartRef, yAxisId: yAxisIds, scope: "gap" });
  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    boundaries: "active-analyses",
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useQuantityNoDataIntervals(analyses, selectedQuantities, "gap", project.id),
  });

  const yAxes = useQuantityYAxes({
    selectedQuantities,
    axisGroupedByUnit: false,
    multiColor: false,
    precision: true,
    axisTitle: (name, color) => (
      <>
        <AxisTitle className={colorClassName(color)}>{name}</AxisTitle> -{" "}
        {t("symptom.chart.header.option.gap")}
      </>
    ),
  });

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const selectedPoints = getMainAxisSelectedPoints(this.series.chart);
      const selectedCount = this.point.selected ? selectedPoints.length : 0;

      const metadata = validateInterface(PointMetadata, this.point.options.custom);
      const humanValueOptions = metadata.type === GAP_POINT_METADATA ? metadata.human : null;

      const quantity = quantitiesByName[this.series.name];
      const hasQuantityField = quantity !== undefined;

      const option = {
        label: t("symptoms.chart.tooltip.selected_chart.gap"),
        value: humanValueOptions
          ? formatNumber(humanValueOptions.value, {
              sign: humanValueOptions.sign ?? undefined,
              precision: humanValueOptions.precision ?? undefined,
              unit: humanValueOptions.unit ?? undefined,
            })
          : "-",
      };

      const isSymptom =
        metadata.type === GAP_POINT_METADATA ? (metadata.isSymptom ?? false) : false;
      const descriptionSymptom = this.point.description ?? null;

      return renderToString(
        <ChartTooltip>
          {selectedCount > 1 ? (
            <Text type="h4">
              {t("measures.chart.tooltip.selectedPoints", {
                count: selectedPoints.length,
              })}
            </Text>
          ) : (
            <>
              <AnalysisDateChartTooltip
                date={this.point.x}
                group={metadata.group}
                label={metadata.label}
                timezone={timeZone}
              />
              {hasQuantityField ? (
                <QuantityChartTooltip
                  name={quantity.name}
                  description={quantity.description}
                  option={option}
                />
              ) : null}
              {descriptionSymptom ? (
                <SymptomQuantityChartTooltip
                  description={descriptionSymptom}
                  isSymptom={isSymptom}
                />
              ) : null}
            </>
          )}
          {"flags" in metadata ? <FlagChartTooltip flags={metadata.flags} /> : null}
        </ChartTooltip>,
      );
    },
    [quantitiesByName, timeZone],
  );

  return React.useMemo(() => {
    const options: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: [initialYAxisOptions],
      },
      chart: {
        zoomType: "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
      },
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [...yAxes, FLAG_AXIS],
      tooltip: {
        formatter: constantTooltipFormatter,
        borderColor: undefined, // Supprimer la couleur de bordure par défaut
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          max: Date.now(),
        },
      },
      series,
    };
    return options;
  }, [initialXAxisOptions, initialYAxisOptions, xBreaks, yAxes, constantTooltipFormatter, series]);
};
export default useOptions;
